import { X, FileText, Download, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useState } from 'react';
import { useInvoicesOfMonth } from '@/hooks/useBilling';
import { billingService, invoiceLetter, INVOICE_TYPE_LABELS } from '@/services/billing';
import type { BillingSummaryRow } from '@/services/billing';
import { salesService } from '@/services/sales';
import { generarFacturaPdf, condicionLabel } from '@/services/invoicePdf';
import type { ComprobanteItem } from '@/services/invoicePdf';
import { formatPrice } from '@/utils/format';
import { useAuthStore } from '@/context/authStore';

/**
 * Los comprobantes que hay detras de una fila del resumen de facturacion.
 *
 * El resumen dice "5 facturas por $X" y esto responde cuales son, que es lo que hace
 * falta cuando el numero no cierra con lo que esperaba el contador.
 */

interface Props {
  row: BillingSummaryRow;
  year: number;
  month: number;
  monthLabel: string;
  onClose: () => void;
}

export default function BillingSummaryDetail({ row, year, month, monthLabel, onClose }: Props) {
  const tenant = useAuthStore((s) => s.tenant);
  const { data: invoices = [], isLoading } = useInvoicesOfMonth(year, month, row.point_of_sale_id);
  const [descargando, setDescargando] = useState<number | null>(null);

  const numero = (posNumber: number, invoiceNumber: number | null) =>
    `${posNumber.toString().padStart(4, '0')}-${(invoiceNumber ?? 0).toString().padStart(8, '0')}`;

  // El PDF necesita las lineas, que viven en la venta: el comprobante solo guarda el total.
  const descargarPdf = async (invoiceId: number, saleId: number | null) => {
    if (!tenant) return;
    setDescargando(invoiceId);
    try {
      const detail = await billingService.getInvoice(invoiceId);

      let items: ComprobanteItem[] = [];
      if (saleId) {
        const sale = await salesService.getSale(saleId);
        const esNota = detail.invoice_type.startsWith('nota_credito');
        const delRubro = sale.items.filter((i) => {
          if (row.applies_to === 'all') return true;
          return !!i.is_service === (row.applies_to === 'services');
        });
        const fuente = delRubro.length > 0 && !esNota ? delRubro : sale.items;
        items = fuente.map((i) => ({
          descripcion: i.product_name ?? `Producto #${i.product_id}`,
          cantidad: Number(i.quantity),
          precioUnitario: Number(i.unit_price),
          subtotal: Number(i.subtotal),
        }));
      }

      await generarFacturaPdf(
        {
          invoiceTypeLabel: INVOICE_TYPE_LABELS[detail.invoice_type],
          letra: invoiceLetter(detail.invoice_type),
          posNumber: detail.pos_number,
          invoiceNumber: detail.invoice_number ?? 0,
          fecha: detail.invoice_date,
          cae: detail.cae ?? '',
          caeDueDate: detail.cae_due_date,
          qrUrl: detail.qr_url,
          emisorCuit: detail.emisor_cuit,
          emisorRazonSocial: detail.emisor_razon_social,
          emisorIvaCondition: detail.emisor_iva_condition,
          receptorNombre: detail.receiver_name,
          receptorDoc: detail.receiver_doc_number,
          receptorCondicion: condicionLabel(detail.receiver_iva_condition),
          receptorDomicilio: detail.receiver_address,
          items,
          total: Number(detail.total),
        },
        tenant,
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.detail ?? 'No se pudo generar el PDF');
    } finally {
      setDescargando(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h2 className="font-semibold text-gray-800 flex items-center gap-2">
              <FileText className="h-4 w-4 text-gray-400" />
              Punto de venta {row.pos_number.toString().padStart(4, '0')} · {row.description}
            </h2>
            <p className="text-sm text-gray-500">
              Comprobantes de {monthLabel} {year}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto p-5">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Cargando…</div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-8 text-gray-400 text-sm">
              No hay comprobantes en este período.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-gray-500 border-b">
                <tr>
                  <th className="pb-2 text-left font-medium">Comprobante</th>
                  <th className="pb-2 text-left font-medium">Fecha</th>
                  <th className="pb-2 text-left font-medium">Cliente</th>
                  <th className="pb-2 text-right font-medium">Importe</th>
                  <th className="pb-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const esNota = inv.invoice_type.startsWith('nota_credito');
                  return (
                    <tr key={inv.id} className="border-b border-gray-50">
                      <td className="py-2">
                        <div className={`font-mono ${esNota ? 'text-red-600' : 'text-gray-800'}`}>
                          {numero(inv.pos_number, inv.invoice_number)}
                        </div>
                        <div className="text-xs text-gray-400">
                          {INVOICE_TYPE_LABELS[inv.invoice_type]} · CAE {inv.cae}
                        </div>
                      </td>
                      <td className="py-2 text-gray-600">
                        {new Date(inv.invoice_date).toLocaleDateString('es-AR')}
                      </td>
                      <td className="py-2 text-gray-600">
                        {inv.receiver_name || 'Consumidor Final'}
                      </td>
                      <td
                        className={`py-2 text-right font-medium ${
                          esNota ? 'text-red-600' : 'text-gray-900'
                        }`}
                      >
                        {esNota ? '−' : ''}
                        {formatPrice(Number(inv.total))}
                      </td>
                      <td className="py-2 text-right">
                        <button
                          onClick={() => descargarPdf(inv.id, inv.sale_id)}
                          disabled={descargando === inv.id}
                          className="p-1.5 text-gray-400 hover:text-gray-700 disabled:opacity-50"
                          title="Descargar PDF"
                        >
                          {descargando === inv.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between text-sm">
          <span className="text-gray-500">
            {row.invoices_count} factura(s)
            {row.credit_notes_count > 0 ? ` · ${row.credit_notes_count} nota(s) de crédito` : ''}
          </span>
          <span className="font-semibold text-gray-900">
            Neto {formatPrice(Number(row.net_total))}
          </span>
        </div>
      </div>
    </div>
  );
}
